import { z } from "zod";
import { supabase } from "../supabase/client";
import { loadStarBalance } from "../economy/starWallet";

export type RewardType = "stars" | "room_decor" | "clothing" | "toy";
export type RewardRarity = "common" | "rare" | "magic";

export interface RewardDefinition {
  id: string;
  type: RewardType;
  name: string;
  quantity: number;
  rarity: RewardRarity;
  assetKey: string;
  weight: number;
  description: string;
}

export interface GachaDrawResult extends RewardDefinition {
  drawIndex: number;
  grantedAt: string;
  totalStars: number;
  clientDrawId: string;
}

export interface DailyGachaState {
  date: string;
  drawsUsed: number;
  maxDraws: 3;
  rewards: GachaDrawResult[];
  source: "cloud" | "local";
}

export interface SurpriseLetterInstance {
  templateId: string;
  title: string;
  recipient: string;
  paragraphs: string[];
  signature: string;
  audioUrl: string | null;
  contentVersion: string;
  generatedAt: string;
}

export const GACHA_REWARDS: RewardDefinition[] = [
  { id: "stars-1", type: "stars", name: "一颗小星光", quantity: 1, rarity: "common", assetKey: "rewardStar", weight: 22, description: "一颗暖暖的小星光" },
  { id: "stars-3", type: "stars", name: "三颗小星光", quantity: 3, rarity: "rare", assetKey: "rewardStar", weight: 15, description: "三颗闪亮的小星光" },
  { id: "stars-5", type: "stars", name: "五颗小星光", quantity: 5, rarity: "magic", assetKey: "rewardStar", weight: 8, description: "一捧魔法星光" },
  { id: "moon-lamp", type: "room_decor", name: "星光小夜灯", quantity: 1, rarity: "rare", assetKey: "starLantern", weight: 10, description: "把柔柔的星光带回小屋" },
  { id: "moon-plant", type: "room_decor", name: "月光盆栽", quantity: 1, rarity: "common", assetKey: "moonPlant", weight: 8, description: "夜里也会发芽的月光植物" },
  { id: "star-cushion", type: "room_decor", name: "星星抱枕", quantity: 1, rarity: "common", assetKey: "starCushion", weight: 7, description: "软乎乎的星星抱枕" },
  { id: "cloud-bunny", type: "toy", name: "云兔玩偶", quantity: 1, rarity: "rare", assetKey: "cloudBunny", weight: 10, description: "会陪阿朵午睡的小玩偶" },
  { id: "toy-telescope", type: "toy", name: "星空望远镜", quantity: 1, rarity: "magic", assetKey: "telescope", weight: 8, description: "用它寻找远方的新星球" },
  { id: "starlight-dress", type: "clothing", name: "星光魔法裙", quantity: 1, rarity: "magic", assetKey: "dress", weight: 7, description: "裙摆上藏着会眨眼的星星" },
  { id: "rainbow-cape", type: "clothing", name: "彩虹小披风", quantity: 1, rarity: "rare", assetKey: "cape", weight: 5, description: "跑起来会留下彩虹的披风" }
];

const drawSchema = z.object({
  reward_id: z.string(),
  reward_type: z.enum(["stars", "room_decor", "clothing", "toy"]),
  reward_name: z.string(),
  quantity: z.coerce.number().int().positive(),
  rarity: z.enum(["common", "rare", "magic"]),
  asset_key: z.string(),
  draw_index: z.coerce.number().int().min(1).max(3),
  total_stars: z.coerce.number().int().nonnegative(),
  granted_at: z.string()
});

const DEFAULT_LETTER: Omit<SurpriseLetterInstance, "audioUrl"> = {
  templateId: "letter-kindergarten-opening-v1",
  title: "彩虹二班的开学信",
  recipient: "亲爱的鹿鹿",
  paragraphs: [
    "恭喜你，今天正式成为幼儿园小二班的小朋友啦！",
    "愿清晨暖暖的阳光，陪你开启属于幼儿园的崭新小故事。",
    "愿你在这里满怀好奇，日日欢笑，收获点点滴滴小小的成长。",
    "希望你的幼儿园时光，如彩虹般绚烂，满是欢喜与精彩！"
  ],
  signature: "爱你的露米和阿朵 ✦",
  contentVersion: "v1",
  generatedAt: "2026-08-25T20:17:00+08:00"
};

function chinaDate() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

function isCloudChild(childId: string | null): childId is string {
  return Boolean(childId && /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(childId));
}

function stateKey(childId: string | null) {
  return `lusky:gacha:${childId || "local-child"}:${chinaDate()}`;
}

function inventoryKey(childId: string | null) {
  return `lusky:inventory:${childId || "local-child"}:v1`;
}

function safeParse<T>(value: string | null, fallback: T): T {
  try { return value ? JSON.parse(value) as T : fallback; } catch { return fallback; }
}

function readLocalState(childId: string | null): DailyGachaState {
  const saved = safeParse<DailyGachaState | null>(localStorage.getItem(stateKey(childId)), null);
  if (!saved || saved.date !== chinaDate()) return { date: chinaDate(), drawsUsed: 0, maxDraws: 3, rewards: [], source: "local" };
  return { ...saved, source: "local", maxDraws: 3 };
}

function weightedPick(candidates: RewardDefinition[]) {
  const total = candidates.reduce((sum, reward) => sum + reward.weight, 0);
  let cursor = Math.random() * total;
  for (const reward of candidates) {
    cursor -= reward.weight;
    if (cursor <= 0) return reward;
  }
  return candidates[candidates.length - 1];
}

function persistInventory(childId: string | null, reward: RewardDefinition, totalStars: number) {
  const owned = safeParse<string[]>(localStorage.getItem(inventoryKey(childId)), []);
  if (reward.type !== "stars" && !owned.includes(reward.id)) {
    owned.push(reward.id);
    localStorage.setItem(inventoryKey(childId), JSON.stringify(owned));
  }

  const room = safeParse<Record<string, unknown>>(localStorage.getItem("lusky-room-v2"), {});
  const listName = reward.type === "clothing" ? "clothes" : reward.type === "toy" ? "toys" : "inventory";
  if (reward.type !== "stars") {
    const list = Array.isArray(room[listName]) ? room[listName] as string[] : [];
    room[listName] = [...new Set([...list, reward.id])];
  }
  room.stars = totalStars;
  localStorage.setItem("lusky-room-v2", JSON.stringify(room));
  localStorage.setItem("lusky:star-total", String(totalStars));
}

function claimLocal(childId: string | null, currentStars: number, clientDrawId: string): GachaDrawResult {
  const state = readLocalState(childId);
  if (state.drawsUsed >= 3) throw new Error("今天的三次惊喜已经全部收好啦。");

  const owned = new Set(safeParse<string[]>(localStorage.getItem(inventoryKey(childId)), []));
  const isThirdGuarantee = state.drawsUsed === 2 && state.rewards.every((reward) => reward.type === "stars");
  const collectibleCandidates = GACHA_REWARDS.filter((reward) => reward.type !== "stars" && !owned.has(reward.id));
  const candidates = isThirdGuarantee && collectibleCandidates.length
    ? collectibleCandidates
    : GACHA_REWARDS.filter((reward) => reward.type === "stars" || !owned.has(reward.id));
  const selected = weightedPick(candidates.length ? candidates : GACHA_REWARDS.filter((reward) => reward.type === "stars"));
  const totalStars = currentStars + (selected.type === "stars" ? selected.quantity : 0);
  const result: GachaDrawResult = {
    ...selected,
    drawIndex: state.drawsUsed + 1,
    grantedAt: new Date().toISOString(),
    totalStars,
    clientDrawId
  };
  const next: DailyGachaState = { ...state, drawsUsed: result.drawIndex, rewards: [...state.rewards, result], source: "local" };
  localStorage.setItem(stateKey(childId), JSON.stringify(next));
  persistInventory(childId, selected, totalStars);
  recordLocalEvent(childId, "surprise.gacha_reward_granted", result);
  return result;
}

function recordLocalEvent(childId: string | null, eventType: string, payload: unknown) {
  const key = `lusky:events:${childId || "local-child"}:v1`;
  const events = safeParse<unknown[]>(localStorage.getItem(key), []);
  events.push({ id: crypto.randomUUID(), eventType, payload, occurredAt: new Date().toISOString() });
  localStorage.setItem(key, JSON.stringify(events.slice(-300)));
}

export async function recordSurpriseInteraction(childId: string | null, eventType: string, payload: Record<string, unknown> = {}) {
  recordLocalEvent(childId, eventType, payload);
  if (!supabase || !isCloudChild(childId)) return;
  await supabase.from("learning_events").insert({
    child_id: childId,
    attempt_id: null,
    client_event_id: crypto.randomUUID(),
    event_type: eventType,
    payload,
    occurred_at: new Date().toISOString()
  });
}

export async function loadDailyGachaState(childId: string | null): Promise<DailyGachaState> {
  if (!supabase || !isCloudChild(childId)) return readLocalState(childId);
  const { data, error } = await supabase
    .from("gacha_draws")
    .select("client_draw_id,draw_index,reward_id,reward_type,reward_name,quantity,rarity,asset_key,total_stars,granted_at")
    .eq("child_id", childId)
    .eq("draw_date", chinaDate())
    .order("draw_index");
  if (error) return readLocalState(childId);
  const rewards = (data ?? []).flatMap((row) => {
    const result = drawSchema.safeParse(row);
    if (!result.success) {
      console.warn("Ignored an incompatible gacha history row", result.error.flatten());
      return [];
    }
    const parsed = result.data;
    const definition = GACHA_REWARDS.find((reward) => reward.id === parsed.reward_id);
    return [{
      ...(definition ?? {
        id: parsed.reward_id,
        type: parsed.reward_type,
        name: parsed.reward_name,
        quantity: parsed.quantity,
        rarity: parsed.rarity,
        assetKey: parsed.asset_key,
        weight: 0,
        description: "来自星芽扭蛋机的惊喜"
      }),
      drawIndex: parsed.draw_index,
      totalStars: parsed.total_stars,
      grantedAt: parsed.granted_at,
      clientDrawId: String(row.client_draw_id)
    } satisfies GachaDrawResult];
  });
  return { date: chinaDate(), drawsUsed: rewards.length, maxDraws: 3, rewards, source: "cloud" };
}

export async function claimDailyGachaReward(childId: string | null, currentStars: number, clientDrawId = crypto.randomUUID()): Promise<GachaDrawResult> {
  if (!supabase || !isCloudChild(childId)) return claimLocal(childId, currentStars, clientDrawId);
  const { data, error } = await supabase.rpc("claim_daily_surprise_reward", { p_child_id: childId, p_client_draw_id: clientDrawId });
  if (error || !data?.[0]) return claimLocal(childId, currentStars, clientDrawId);
  const parsed = drawSchema.parse(data[0]);
  const definition = GACHA_REWARDS.find((reward) => reward.id === parsed.reward_id);
  const result: GachaDrawResult = {
    ...(definition ?? {
      id: parsed.reward_id,
      type: parsed.reward_type,
      name: parsed.reward_name,
      quantity: parsed.quantity,
      rarity: parsed.rarity,
      assetKey: parsed.asset_key,
      weight: 0,
      description: "来自星芽扭蛋机的惊喜"
    }),
    drawIndex: parsed.draw_index,
    totalStars: await loadStarBalance(childId, parsed.total_stars),
    grantedAt: parsed.granted_at,
    clientDrawId
  };
  persistInventory(childId, result, result.totalStars);
  return result;
}

export async function loadSurpriseLetter(childId: string | null, fallbackAudioUrl: string): Promise<SurpriseLetterInstance> {
  const localKey = `lusky:surprise-letter:${childId || "local-child"}:${DEFAULT_LETTER.templateId}`;
  const local = safeParse<SurpriseLetterInstance | null>(localStorage.getItem(localKey), null);
  if (local) return { ...local, audioUrl: local.audioUrl || fallbackAudioUrl };

  if (supabase && isCloudChild(childId)) {
    const { data } = await supabase
      .from("surprise_instances")
      .select("template_id,title,text_content,generated_audio_key,generated_audio_metadata,content_version,generated_at")
      .eq("child_id", childId)
      .eq("template_id", DEFAULT_LETTER.templateId)
      .limit(1)
      .maybeSingle();
    if (data) {
      const content = data.text_content as { recipient?: string; paragraphs?: string[]; signature?: string } | null;
      const audioMetadata = data.generated_audio_metadata as { public_path?: string } | null;
      let audioUrl: string | null = audioMetadata?.public_path || fallbackAudioUrl;
      if (data.generated_audio_key) {
        const signed = await supabase.storage.from("child-media").createSignedUrl(String(data.generated_audio_key), 1800);
        if (!signed.error) audioUrl = signed.data.signedUrl;
      }
      const instance: SurpriseLetterInstance = {
        templateId: String(data.template_id),
        title: String(data.title),
        recipient: content?.recipient || DEFAULT_LETTER.recipient,
        paragraphs: content?.paragraphs || DEFAULT_LETTER.paragraphs,
        signature: content?.signature || DEFAULT_LETTER.signature,
        audioUrl,
        contentVersion: String(data.content_version),
        generatedAt: String(data.generated_at)
      };
      localStorage.setItem(localKey, JSON.stringify(instance));
      return instance;
    }

    const availableOn = chinaDate();
    const persisted = await supabase.from("surprise_instances").upsert({
      child_id: childId,
      template_id: DEFAULT_LETTER.templateId,
      available_on: availableOn,
      title: DEFAULT_LETTER.title,
      text_content: {
        recipient: DEFAULT_LETTER.recipient,
        paragraphs: DEFAULT_LETTER.paragraphs,
        signature: DEFAULT_LETTER.signature
      },
      generated_audio_key: null,
      generated_audio_metadata: {
        public_path: fallbackAudioUrl,
        voice_role: "teacher",
        persisted: true
      },
      content_version: DEFAULT_LETTER.contentVersion,
      generated_at: DEFAULT_LETTER.generatedAt
    }, { onConflict: "child_id,template_id,available_on" });
    if (!persisted.error) {
      await recordSurpriseInteraction(childId, "surprise.letter_persisted", { template_id: DEFAULT_LETTER.templateId });
    }
  }

  const instance: SurpriseLetterInstance = { ...DEFAULT_LETTER, audioUrl: fallbackAudioUrl };
  localStorage.setItem(localKey, JSON.stringify(instance));
  return instance;
}